import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, Download, Eye, CheckCircle, XCircle, Clock, 
  Mail, Phone, MapPin, GraduationCap, Calendar, Filter,
  ArrowLeft, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const AdminTutorApplicationsView = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected
  const [selectedApp, setSelectedApp] = useState(null);
  const [authError, setAuthError] = useState(false);

  useEffect(() => {
    fetchApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/fresh-applications`, {
        credentials: 'include'
      });

      if (response.status === 401 || response.status === 403) {
        setAuthError(true);
        setLoading(false);
        toast.error('Please log in as admin to view applications');
        setTimeout(() => navigate('/admin/login'), 2000);
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch applications');

      const data = await response.json();
      setApplications(data.applications || []);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load tutor applications');
      setLoading(false);
    }
  };

  const downloadCV = async (cvUrl, applicantName) => {
    try {
      // Ensure full URL for file access (files are served by backend)
      const fullUrl = cvUrl.startsWith('http') ? cvUrl : `${BACKEND_URL}${cvUrl}`;
      window.open(fullUrl, '_blank');
      toast.success(`Opening CV for ${applicantName}`);
    } catch (error) {
      toast.error('Failed to open CV');
    }
  };

  const handleApprove = async (applicationId, fullName) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/fresh-applications/${applicationId}/approve`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );

      if (!response.ok) throw new Error('Failed to approve');

      const data = await response.json();
      toast.success(data.message || `${fullName} approved!`);
      fetchApplications();
    } catch (error) {
      toast.error('Failed to approve application');
    }
  };

  const handleReject = async (applicationId) => {
    if (!window.confirm('Are you sure you want to reject this application?')) return;

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/fresh-applications/${applicationId}/reject`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: 'Not suitable at this time' })
        }
      );

      if (!response.ok) throw new Error('Failed to reject');

      toast.success('Application rejected');
      fetchApplications();
    } catch (error) {
      toast.error('Failed to reject application');
    }
  };

  const getFilteredApplications = () => {
    if (filter === 'all') return applications;
    return applications.filter(app => app.status === filter);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };

    const icons = {
      pending: <Clock className="w-4 h-4" />,
      approved: <CheckCircle className="w-4 h-4" />,
      rejected: <XCircle className="w-4 h-4" />
    };

    return (
      <span className={`px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1 ${styles[status]}`}>
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const filteredApps = getFilteredApplications();

  // SEC-002: cookie-based admin auth — if not authorised, redirect (handled by fetch above).
  if (authError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <FileText className="w-16 h-16 text-yellow-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Admin sign-in required</h2>
          <p className="text-gray-700 mb-6">Please log in with an admin account to view tutor applications.</p>
          <Button onClick={() => navigate('/admin/login')} className="w-full btn-primary">
            Go to admin login
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/admin/applications')}
                variant="ghost"
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Tutor Applications</h1>
                <p className="text-gray-600">Review applications and download CVs</p>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-white rounded-lg p-2 shadow">
              <Filter className="w-5 h-5 text-gray-500" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="border-none bg-transparent focus:ring-0 cursor-pointer"
              >
                <option value="all">All ({applications.length})</option>
                <option value="pending">Pending ({applications.filter(a => a.status === 'pending').length})</option>
                <option value="approved">Approved ({applications.filter(a => a.status === 'approved').length})</option>
                <option value="rejected">Rejected ({applications.filter(a => a.status === 'rejected').length})</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg p-6 shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Applications</p>
                <p className="text-2xl font-bold">{applications.length}</p>
              </div>
              <FileText className="w-10 h-10 text-blue-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Pending Review</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {applications.filter(a => a.status === 'pending').length}
                </p>
              </div>
              <Clock className="w-10 h-10 text-yellow-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Approved</p>
                <p className="text-2xl font-bold text-green-600">
                  {applications.filter(a => a.status === 'approved').length}
                </p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">With CV</p>
                <p className="text-2xl font-bold text-purple-600">
                  {applications.filter(a => a.cvUrl).length}
                </p>
              </div>
              <Download className="w-10 h-10 text-purple-500" />
            </div>
          </div>
        </div>

        {/* Applications List */}
        {filteredApps.length === 0 ? (
          <div className="bg-white rounded-lg p-12 text-center shadow">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Applications Found</h3>
            <p className="text-gray-600">
              {filter === 'all' 
                ? 'No tutor applications yet.' 
                : `No ${filter} applications.`}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredApps.map((app) => (
              <div key={app.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-gray-900">{app.fullName}</h3>
                        {getStatusBadge(app.status)}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          <a href={`mailto:${app.email}`} className="hover:text-primary">
                            {app.email}
                          </a>
                        </div>
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          <span>{app.phone}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          <span>{app.suburb}, {app.city}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <GraduationCap className="w-4 h-4" />
                          <span>{app.degree} - {app.university}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-2">
                      {app.cvUrl && (
                        <Button
                          onClick={() => downloadCV(app.cvUrl, app.fullName)}
                          className="btn-primary flex items-center gap-2"
                        >
                          <Download className="w-4 h-4" />
                          Download CV
                        </Button>
                      )}
                      <Button
                        onClick={() => setSelectedApp(selectedApp?.id === app.id ? null : app)}
                        variant="outline"
                        className="flex items-center gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        {selectedApp?.id === app.id ? 'Hide Details' : 'View Details'}
                      </Button>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {selectedApp?.id === app.id && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-semibold mb-3">Education & Experience</h4>
                          <div className="space-y-2 text-sm">
                            <div>
                              <span className="font-medium">ATAR:</span> {app.atar || 'N/A'}
                            </div>
                            <div>
                              <span className="font-medium">Current Occupation:</span> {app.currentOccupation}
                            </div>
                            <div>
                              <span className="font-medium">Subjects:</span> {app.subjects?.join(', ') || 'N/A'}
                            </div>
                            {app.yearLevelsCanTeach && (
                              <div>
                                <span className="font-medium">Can Teach:</span> {app.yearLevelsCanTeach.join(', ')}
                              </div>
                            )}
                          </div>
                        </div>

                        <div>
                          <h4 className="font-semibold mb-3">Availability & Preferences</h4>
                          <div className="space-y-2 text-sm">
                            <div>
                              <span className="font-medium">Tutoring Mode:</span> {app.tutoringMode}
                            </div>
                            <div>
                              <span className="font-medium">Preferred Hours:</span> {app.preferredHours}
                            </div>
                            <div>
                              <span className="font-medium">Weekday Availability:</span> {app.weekdayAvailability}
                            </div>
                            <div>
                              <span className="font-medium">Weekend Availability:</span> {app.weekendAvailability}
                            </div>
                          </div>
                        </div>

                        <div className="md:col-span-2">
                          <h4 className="font-semibold mb-3">Teaching Experience & Motivation</h4>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm whitespace-pre-wrap">{app.teachingExperience || 'No experience provided'}</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4 mt-3">
                            <p className="text-sm whitespace-pre-wrap">{app.motivationStatement || 'No motivation statement'}</p>
                          </div>
                        </div>

                        {app.linkedIn && (
                          <div className="md:col-span-2">
                            <a
                              href={app.linkedIn}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline flex items-center gap-2"
                            >
                              <ExternalLink className="w-4 h-4" />
                              View LinkedIn Profile
                            </a>
                          </div>
                        )}

                        <div className="md:col-span-2 flex items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>Applied: {new Date(app.createdAt).toLocaleDateString()}</span>
                          </div>
                          {app.cvUrl && (
                            <div className="flex items-center gap-2 text-green-600">
                              <FileText className="w-4 h-4" />
                              <span>CV Uploaded ✓</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTutorApplicationsView;
